import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, FolderOpen, Settings2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { CaseCard } from '../components/cases/CaseCard';
import { CaseFilters } from '../components/cases/CaseFilters';
import { CreateCaseModal } from '../components/cases/CreateCaseModal';
import { ReasonsManagementModal } from '../components/ReasonsManagementModal';
import { casesApi } from '../lib/api';
import type { Case, CaseStatus } from '../types';
import svgPaths from '../imports/SidebarIconPaths';

function LogoIcon() {
  return (
    <div className="h-[24.618px] relative shrink-0 w-[26.71px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 27 25">
        <path d={svgPaths.p3311a400} fill="#151519" id="Union" />
      </svg>
    </div>
  );
}

function DarwinText() {
  return (
    <div className="h-[19.554px] relative shrink-0 w-[83.253px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 84 20">
        <g clipPath="url(#clip0_3_94814)">
          <g id="Group">
            <path d={svgPaths.p8385900} fill="#1B1B1F" id="Vector" />
            <path d={svgPaths.pced9f70} fill="#1B1B1F" id="Vector_2" />
            <path d={svgPaths.p5a1d00} fill="#1B1B1F" id="Vector_3" />
            <path d={svgPaths.p1fb9c9f0} fill="#1B1B1F" id="Vector_4" />
            <path d={svgPaths.p211af00} fill="#1B1B1F" id="Vector_5" />
            <path d={svgPaths.p35c86c00} fill="#1B1B1F" id="Vector_6" />
          </g>
          <path d={svgPaths.pef77900} fill="#151519" id="Vector_7" />
        </g>
        <defs>
          <clipPath id="clip0_3_94814">
            <rect fill="white" height="19.5537" width="83.2533" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function RedactitText() {
  return (
    <div className="h-[20.127px] overflow-clip w-[87.699px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 88 21">
        <g id="Layer 1">
          <path d={svgPaths.p4752680} fill="#7035C4" id="Vector" />
          <path d={svgPaths.p260cc900} fill="#7035C4" id="Vector_2" />
          <path d={svgPaths.p2e772a00} fill="#7035C4" id="Vector_3" />
          <path d={svgPaths.p3937bc00} fill="#7035C4" id="Vector_4" />
          <g id="Group">
            <path d={svgPaths.p3d000e40} fill="#7035C4" id="Vector_5" />
            <path d={svgPaths.p360c0400} fill="#7035C4" id="Vector_6" />
          </g>
          <path d={svgPaths.pf08f700} fill="#7035C4" id="Vector_7" />
          <path d={svgPaths.p2d2a7a80} fill="#7035C4" id="Vector_8" />
          <path d={svgPaths.pfa6a00} fill="#7035C4" id="Vector_9" />
        </g>
      </svg>
    </div>
  );
}

export function CasesHomePage() {
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isReasonsModalOpen, setIsReasonsModalOpen] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CaseStatus | 'all'>('all');
  const [ownerFilter, setOwnerFilter] = useState('');

  // Extract unique owners from cases
  const owners = useMemo(() => {
    const uniqueOwners = [...new Set(cases.map((c) => c.owner))];
    return uniqueOwners.sort();
  }, [cases]);

  // Filter cases based on search and filters
  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      const matchesSearch =
        !search ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.notes?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchesOwner = !ownerFilter || c.owner === ownerFilter;
      return matchesSearch && matchesStatus && matchesOwner;
    });
  }, [cases, search, statusFilter, ownerFilter]);

  // Fetch cases on mount
  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await casesApi.getCases({ limit: 100 });
      setCases(response.cases);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
      setError('Failed to load cases. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaseClick = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  const handleCaseCreated = (newCase: Case) => {
    setCases((prev) => [newCase, ...prev]);
    setIsCreateModalOpen(false);
    navigate(`/cases/${newCase.id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <LogoIcon />
              <DarwinText />
              <RedactitText />
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsReasonsModalOpen(true)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Manage Reasons"
                title="Manage Redaction Reasons"
              >
                <Settings2 className="w-5 h-5 text-foreground" />
              </button>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Case
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="mb-8">
          <CaseFilters
            search={search}
            onSearchChange={setSearch}
            status={statusFilter}
            onStatusChange={setStatusFilter}
            owner={ownerFilter}
            onOwnerChange={setOwnerFilter}
            owners={owners}
          />
        </div>

        {/* Cases Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-destructive mb-4">{error}</p>
            <Button variant="outline" onClick={fetchCases}>
              Try Again
            </Button>
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            {cases.length === 0 ? (
              <>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No cases yet
                </h3>
                <p className="text-muted-foreground mb-6">
                  Create your first case to get started with document redaction.
                </p>
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Case
                </Button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No matching cases
                </h3>
                <p className="text-muted-foreground">
                  Try adjusting your search or filter criteria.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCases.map((caseData) => (
              <CaseCard
                key={caseData.id}
                caseData={caseData}
                onClick={handleCaseClick}
              />
            ))}
          </div>
        )}
      </main>

      {/* Create Case Modal */}
      <CreateCaseModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCaseCreated={handleCaseCreated}
      />

      {/* Reasons Management Modal */}
      <ReasonsManagementModal
        open={isReasonsModalOpen}
        onClose={() => setIsReasonsModalOpen(false)}
      />
    </div>
  );
}
